import { useState } from "react";
import { api, type Board } from "../api.ts";
import { C, mono, sans } from "../theme.ts";

// Zero obligation. No dates, no tiers, no state, no counts, and nothing here
// ever appears in a brief. An item becomes a task only when it becomes real.

export function Collections({ board, onChanged, onOpen }: { board: Board; onChanged: () => void; onOpen: (id: string) => void }) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = async (id: string) => {
    const text = (drafts[id] ?? "").trim();
    if (!text) return;
    setBusy(id);
    try {
      await api.addCollectionItem(id, text);
      setDrafts((d) => ({ ...d, [id]: "" }));
      onChanged();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };

  const promote = async (itemId: string, valueId: string) => {
    setBusy(itemId);
    try {
      const created = await api.promote(itemId, { value_id: valueId, tier: "nice" });
      onChanged();
      onOpen(created.id);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };

  return (
    <div style={{ padding: "0 18px 8px" }}>
      {error && <p style={{ color: C.alert, fontSize: 13 }}>{error}</p>}

      {board.collections.map((c) => (
        <section key={c.id} style={{ marginBottom: 30 }}>
          <div style={{ borderLeft: `3px solid ${c.value_colour}`, paddingLeft: 10 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{c.name}</h2>
            <p style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, margin: "3px 0 0" }}>{c.value_name}</p>
          </div>

          <div style={{ marginTop: 12 }}>
            {c.items.map((it) => (
              <div key={it.id} style={{
                display: "flex", gap: 10, alignItems: "baseline",
                padding: "9px 0", borderBottom: `1px solid ${C.rule}`,
              }}>
                <span style={{ fontSize: 14, flex: 1, lineHeight: 1.45, opacity: it.promoted_task_id ? 0.5 : 1 }}>
                  {it.text}
                </span>
                {it.promoted_task_id ? (
                  <button onClick={() => onOpen(it.promoted_task_id!)} style={{
                    fontFamily: mono, fontSize: 11, color: C.inkFaint, background: "none",
                    border: "none", cursor: "pointer", padding: 0,
                  }}>{it.promoted_task_id}</button>
                ) : (
                  <button disabled={busy === it.id} onClick={() => promote(it.id, c.value_id)} style={{
                    fontFamily: mono, fontSize: 11, color: C.inkFaint, background: "none",
                    border: `1px solid ${C.rule}`, borderRadius: 99, padding: "3px 9px", cursor: "pointer",
                  }}>make it a task</button>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input
              value={drafts[c.id] ?? ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") add(c.id); }}
              placeholder="add a line"
              style={{
                flex: 1, fontFamily: sans, fontSize: 14, padding: "9px 12px",
                borderRadius: 10, border: `1px solid ${C.rule}`, background: C.card,
              }}
            />
            <button disabled={busy === c.id || !(drafts[c.id] ?? "").trim()} onClick={() => add(c.id)} style={{
              padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.ruleStrong}`,
              background: C.card, fontFamily: sans, fontSize: 14, cursor: "pointer",
              opacity: (drafts[c.id] ?? "").trim() ? 1 : 0.4,
            }}>add</button>
          </div>
        </section>
      ))}
    </div>
  );
}
